// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import updateXCheckoutState from "@awc/App/Handlers/Checkout/UpdateLocalState";
import { CustomAppState } from "@awc/App/Store/Reducers";
import { ShippingMethodFormId } from "@awc/XDL/constants";
import { parseBoolean } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import addDays from "@insite/client-framework/Common/Utilities/addDays";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import setIsPreloadingData from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetIsPreloadingData";
import setRequestedDeliveryDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedDeliveryDate";
import setRequestedPickUpDate from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetRequestedPickUpDate";
import setShippingMethod from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/SetShippingMethod";
import translate from "@insite/client-framework/Translate";
import { CarrierDto, ShipViaDto } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: CustomAppState) => {
    const settingsCollection = getSettingsCollection(state);
    const { session } = state.context;
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    return {
        cart: cartState.value,
        showShippingMethod: session.fulfillmentMethod === FulfillmentMethod.Ship,
        showPickUpDate:
            settingsCollection.accountSettings.enableWarehousePickup &&
            settingsCollection.cartSettings.enableRequestPickUpDate &&
            session.fulfillmentMethod === FulfillmentMethod.PickUp,
        session,
        accountSettings: settingsCollection.accountSettings,
        cartSettings: settingsCollection.cartSettings,
        allowEmptyShipping: settingsCollection.accountSettings.allowEmptyShipping,
        // XNG-Change
        shippingAccountNum: state.xCheckout.shipping.shippingAccountNumber,
        useShippingAccountNum: state.xCheckout.shipping.useShippingAccountNumber,
        fulfillmentMethod: session.fulfillmentMethod,
        showUseShippingAccountNumCheckbox: parseBoolean(cartState.value?.properties?.["useShippingAccountNumber"]),
        shipToEditMode: state.xCheckout.addresses.shipToEditMode,
        billToEditMode: state.xCheckout.addresses.billToEditMode,
    };
};

const mapDispatchToProps = {
    setShippingMethod,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    setIsPreloadingData,
    // XNG-Changes
    updateXCheckoutState,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutShippingCarrierServiceStyles {
    container?: GridContainerProps;
    centeringWrapper?: InjectableCss;
    noCarriersFoundGridItem?: GridItemProps;
    noCarriersFoundText?: TypographyPresentationProps;
    carrierGridItem?: GridItemProps;
    carrierSelect?: SelectPresentationProps;
    serviceGridItem?: GridItemProps;
    serviceSelect?: SelectPresentationProps;
    deliveryDateGridItem?: GridItemProps;
    deliveryDatePicker?: DatePickerPresentationProps;
    pickUpDateGridItem?: GridItemProps;
    pickUpDatePicker?: DatePickerPresentationProps;
    // XNG-Change
    orderProcessInfo?: TypographyPresentationProps;
    shippingAccountNumberField?: TextFieldPresentationProps;
    sectionTitle?: InjectableCss;
    useShippingAccountGridItem?: GridItemProps;
    processInfoGridItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    shippingVerificationMsg?: TypographyPresentationProps;
    useShippingNumberCheckbox?: CheckboxPresentationProps;
}

export const checkoutShippingCarrierServiceStyles: CheckoutShippingCarrierServiceStyles = {
    container: { gap: 20 },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 450px;
        `,
    },
    noCarriersFoundGridItem: { width: 12 },
    carrierGridItem: { width: [6, 6, 6, 3, 3] },
    serviceGridItem: { width: [6, 6, 6, 3, 3] },
    deliveryDateGridItem: { width: [12, 12, 12, 6, 6] },
    pickUpDateGridItem: { width: [12, 12, 12, 6, 6] },
};

/**
 * @deprecated Use checkoutShippingCarrierServiceStyles instead.
 */
export const checkoutShippingCarrierService = checkoutShippingCarrierServiceStyles;
const styles = checkoutShippingCarrierServiceStyles;

const CheckoutReviewAndSubmitCarrierService: FC<Props> = ({
    cart,
    cartSettings,
    setRequestedDeliveryDate,
    setRequestedPickUpDate,
    showShippingMethod,
    setShippingMethod,
    showPickUpDate,
    setIsPreloadingData,
    allowEmptyShipping,
    // XNG-Change
    session,
    updateXCheckoutState,
    shippingAccountNum,
    // useShippingAccountNum,
    // fulfillmentMethod,
    showUseShippingAccountNumCheckbox,
    shipToEditMode,
    billToEditMode,
}) => {
    const [selectedCarrier, setSelectedCarrier] = useState<CarrierDto | null>(null);
    const [selectedShipVia, setSelectedShipVia] = useState<ShipViaDto | null>(null);
    // XNG-Change
    const [useShippingAccountNumber, setUseShippingAccountNumber] = useState(showUseShippingAccountNumCheckbox);
    const [shippingAccountNumber, setShippingAccountNumber] = useState(
        cart?.properties?.shippingAccountNumber || shippingAccountNum,
    );
    useEffect(() => {
        if (cart) {
            setSelectedCarrier(cart.carrier);
            setSelectedShipVia(cart.shipVia);
        }
    }, [cart?.carrier, cart?.shipVia]);

    // XNG-Change
    if (shipToEditMode || billToEditMode) {
        return null;
    }

    if (!cart || (cart.lineCount && (!cart.cartLines || cart.cartLines.length === 0)) || !cart.carriers) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner data-test-selector="checkoutShipping_carrierServiceLoading" />
            </StyledWrapper>
        );
    }

    const carrierChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        const selectedCarrier = cart.carriers!.find(c => c.id === event.currentTarget.value)!;
        const defaultShipVias = selectedCarrier.shipVias!.filter(o => o.isDefault);
        const shipVia = defaultShipVias.length > 0 ? defaultShipVias[0] : selectedCarrier.shipVias![0];
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedCarrier(selectedCarrier);
        setSelectedShipVia(shipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };
    const shipViaChangeHandler = (event: ChangeEvent<HTMLSelectElement>) => {
        if (!selectedCarrier) {
            return;
        }
        const selectedShipVia = selectedCarrier.shipVias!.find(s => s.id === event.currentTarget.value)!;
        setIsPreloadingData({ isPreloadingData: true });
        setSelectedShipVia(selectedShipVia);
        setShippingMethod({
            carrier: selectedCarrier,
            shipVia: selectedShipVia,
            onComplete: () => {
                setIsPreloadingData({ isPreloadingData: false });
            },
        });
    };

    const handleRequestPickUpDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedPickUpDate({
            requestedPickUpDate: selectedDay,
            requestedPickUpDateDisabled: selectedDayDisabled,
        });
    };
    const handleRequestDeliveryDateChanged = ({ selectedDay, selectedDayDisabled }: DatePickerState) => {
        setRequestedDeliveryDate({
            requestedDeliveryDate: selectedDay,
            requestedDeliveryDateDisabled: selectedDayDisabled,
        });
    };
    // XNG-Change
    const toggleShippingAccountNumber = () => {
        const useShippingAccountNumberValue = useShippingAccountNumber;
        setUseShippingAccountNumber(!useShippingAccountNumberValue);
        updateXCheckoutState({ shipping: { useShippingAccountNumber: !useShippingAccountNumberValue } });
    };
    const handleShippingAccountNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        updateXCheckoutState({ shipping: { shippingAccountNumber: value } });
        setShippingAccountNumber(value);
    };

    return (
        <>
            <GridContainer {...styles.container}>
                {/* XNG-Change: add title and checkbox for use account number */}
                <GridItem width={12}>
                    <Typography as="h2" {...styles.sectionTitle}>
                        {session.fulfillmentMethod !== FulfillmentMethod.PickUp
                            ? translate("Shipping Method")
                            : translate("Order Notes")}
                    </Typography>
                </GridItem>
                <GridContainer {...styles.innerContainer}>
                    <>
                        {session.fulfillmentMethod !== FulfillmentMethod.PickUp && (
                            <GridItem width={12}>
                                <Checkbox
                                    {...styles.useShippingNumberCheckbox}
                                    onChange={toggleShippingAccountNumber}
                                    checked={useShippingAccountNumber}
                                >
                                    {translate("Use Shipping Account Number")}
                                </Checkbox>
                            </GridItem>
                        )}
                        {useShippingAccountNumber && (
                            <GridItem {...styles.useShippingAccountGridItem}>
                                <TextField
                                    label={translate("Shipping Account Number")}
                                    onChange={handleShippingAccountNumberChange}
                                    value={shippingAccountNumber}
                                    maxLength={30}
                                    type="text"
                                    required
                                    {...styles.shippingAccountNumberField}
                                    {...{ formId: ShippingMethodFormId }}
                                />
                                <Typography {...styles.shippingVerificationMsg}>
                                    {siteMessage("Checkout_ShippingAccountVerificationMessage")}
                                </Typography>
                            </GridItem>
                        )}
                    </>

                    {showShippingMethod && (
                        <>
                            {cart.carriers!.length === 0 && !allowEmptyShipping && (
                                <GridItem {...styles.noCarriersFoundGridItem}>
                                    <Typography {...styles.noCarriersFoundText}>
                                        {siteMessage("ReviewAndPay_NoCarriersFound")}
                                    </Typography>
                                </GridItem>
                            )}
                            {cart.carriers!.length > 0 && (
                                <>
                                    <GridItem {...styles.carrierGridItem}>
                                        <Select
                                            label={translate("Select Carrier")}
                                            {...styles.carrierSelect}
                                            value={selectedCarrier?.id || ""}
                                            onChange={carrierChangeHandler}
                                            data-test-selector="checkoutReviewAndSubmit_carrierSelect"
                                            name="carrier"
                                        >
                                            {cart.carriers!.map(c => {
                                                const id = c.id!.toString();
                                                return (
                                                    <option key={id} value={id}>
                                                        {c.description}
                                                    </option>
                                                );
                                            })}
                                        </Select>
                                    </GridItem>
                                    <GridItem {...styles.serviceGridItem}>
                                        <Select
                                            label={translate("Select Service")}
                                            {...styles.serviceSelect}
                                            value={selectedShipVia?.id || ""}
                                            onChange={shipViaChangeHandler}
                                            data-test-selector="checkoutReviewAndSubmit_shippingServiceSelect"
                                        >
                                            {selectedCarrier &&
                                                selectedCarrier.shipVias!.map(s => {
                                                    const id = s.id.toString();
                                                    return (
                                                        <option key={id} value={id}>
                                                            {s.description}
                                                        </option>
                                                    );
                                                })}
                                        </Select>
                                    </GridItem>
                                </>
                            )}
                            {cartSettings.canRequestDeliveryDate && (
                                <GridItem
                                    {...styles.deliveryDateGridItem}
                                    data-test-selector="checkoutShippingRequestedDeliveryDate"
                                >
                                    <DatePicker
                                        label={`${translate("Request Delivery Date")} (${translate("optional")})`}
                                        hint={siteMessage("Checkout_RequestedDeliveryDateInformation")}
                                        {...styles.deliveryDatePicker}
                                        selectedDay={cart.requestedDeliveryDateDisplay!}
                                        dateTimePickerProps={{
                                            minDate: new Date(),
                                            maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                            monthAriaLabel: translate("Request delivery date month"),
                                            dayAriaLabel: translate("Request delivery date day"),
                                            yearAriaLabel: translate("Request delivery date year"),
                                            ...styles.deliveryDatePicker?.dateTimePickerProps,
                                        }}
                                        onDayChange={handleRequestDeliveryDateChanged}
                                    />
                                </GridItem>
                            )}
                        </>
                    )}
                    {showPickUpDate && (
                        <>
                            <GridItem
                                {...styles.pickUpDateGridItem}
                                data-test-selector="checkoutPickUpRequestedDeliveryDate"
                            >
                                <DatePicker
                                    label={`${translate("Request Pick Up Date")} (${translate("optional")})`}
                                    hint={siteMessage("Checkout_RequestedPickupDateInformation")}
                                    {...styles.pickUpDatePicker}
                                    selectedDay={cart.requestedPickupDateDisplay!}
                                    dateTimePickerProps={{
                                        minDate: new Date(),
                                        maxDate: addDays(new Date(), cartSettings.maximumDeliveryPeriod),
                                        ...styles.pickUpDatePicker?.dateTimePickerProps,
                                    }}
                                    onDayChange={handleRequestPickUpDateChanged}
                                    monthAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                        "Enter month, format M M",
                                    )}`}
                                    dayAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                        "Enter day, format D D",
                                    )}`}
                                    yearAriaLabel={`${translate("Request Pick Up Date optional")}. ${translate(
                                        "Enter year, format Y Y Y Y",
                                    )}`}
                                />
                            </GridItem>
                        </>
                    )}
                    {/* XNG-Change add notes + custom message */}
                    <GridItem width={12}>
                        {createWidgetElement("CheckoutShipping/CheckoutShippingOrderNotesEntry", {
                            fields: {},
                        })}
                    </GridItem>
                    {session.fulfillmentMethod !== FulfillmentMethod.PickUp && (
                        <>
                            <GridItem {...styles.processInfoGridItem}>
                                <Typography {...styles.orderProcessInfo}>
                                    {siteMessage("Checkout_OrderProcessingInformation")}
                                </Typography>
                            </GridItem>
                        </>
                    )}
                </GridContainer>
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitCarrierService),
    definition: {
        displayName: "Carrier & Service",
        group: "Checkout - Review & Submit",
        // XNG-Change
        allowedContexts: ["CheckoutReviewAndSubmitPage", "CheckoutShippingPage"],
    },
};

export default widgetModule;
